import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material'
import {
  Mutation,
  Query,
  Share,
  LeadImport,
} from '@promoboxx/graphql-gateway-types'
import { Formik, FormikProps } from 'formik'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import React, { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router'

import analytics from '@src/analytics'
import ShareSettingForm, {
  ShareSettingDialogFormModel,
  ShareSettingDialogFormValues,
} from '@src/components/content/organicCampaignDetails/ShareSettingForm'
import MobileFullScreenDialog from '@src/components/shared/MobileFullScreenDialog'
import { buildShareDialogPayload } from '@src/components/shareDialog/helpers'
import {
  DELETE_SHARE_MUTATION,
  GET_SHARE_QUERY,
  SHARE_FIELDS,
  UPDATE_SHARE_MUTATION,
} from '@src/components/shareDialog/queries'
import ShareStats from '@src/components/shareDialog/ShareStats'
import { useShareDialog } from '@src/components/shareDialog/useShareDialog'
import { invalidateApolloQuery } from '@src/config/ApolloClient'
import { useCustomFields } from '@src/customFields/customFieldsContext'
import { useSimpleConfirmDialog } from '@src/lib/SimpleConfirmDialog'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'
import useRetailerAccountSlugFromRoute from '@src/lib/useRetailerAccountSlugFromRoute'
import { useRetailerAccountSocialAccounts } from '@src/lib/useRetailerAccountSocialAccounts'
import { formatDate } from '@src/lib/Utils'

import SocialPreview from '../SocialPreview'

const ShareDialog: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { closeShareDialog, publish } = useShareDialog()
  const { confirm } = useSimpleConfirmDialog()
  const location = useLocation()
  const retailerAccountSlug = useRetailerAccountSlugFromRoute()
  const editShareId = new URLSearchParams(location.search).get('editShareId')
  const editShareType = new URLSearchParams(location.search).get(
    'editShareType',
  )
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()

  const { data, loading: shareLoading } = useQuery<Query>(GET_SHARE_QUERY, {
    variables: {
      shareId: editShareId,
      shareType: editShareType,
      params: {
        fields: SHARE_FIELDS,
      },
    },
    skip: !editShareId || !editShareType,
  })
  const customFieldsContext = useCustomFields()

  const loading = shareLoading || customFieldsContext.loading

  const [updateShare] = useMutation<Mutation>(UPDATE_SHARE_MUTATION)
  const [deleteShare] = useMutation<Mutation>(DELETE_SHARE_MUTATION)
  const formikRef = useRef<FormikProps<ShareSettingDialogFormValues> | null>(
    null,
  )

  const share: Share | undefined = editShareId ? data?.getShare : undefined
  const shareSetting = share ? data?.getShare.share_setting : undefined
  const socialAccounts = useRetailerAccountSocialAccounts()

  const retailer = useMemo(() => {
    return ensureCurrentRetailerAccount.retailers?.find(
      (x) => x.brand?.id === data?.getShareSetting?.campaign?.brand?.id,
    )
  }, [
    ensureCurrentRetailerAccount.retailers,
    data?.getShareSetting?.campaign?.brand?.id,
  ])

  useEffect(() => {
    if (share && shareSetting) {
      const model: ShareSettingDialogFormModel = {
        message: share.message || share?.text || '',
        link: share.link || '',
        share_url_type: share.share_setting?.share_url_type || '',
        allow_retailer_url_override:
          share.share_setting?.allow_retailer_url_override || false,
        facebookPageId: share?.facebook_page_id || '-1',
        twitterAccountId: share?.twitter_account_id || '-1',
        instagramPageId: share?.facebook_page_id || '-1',
        scheduled_for: share.scheduled_for || '',
        selected_imports:
          share.lead_imports?.map((el: LeadImport) => el.id) || [],
        subject: share.subject || '',
        child_attachments:
          share.share_setting?.child_attachments?.map((x) => {
            return {
              id: x.id,
              name: x.name || '',
              description: x.description || '',
              url: x.url || '',
              link: x.link || '',
              allow_retailer_url_override:
                x.allow_retailer_url_override || false,
              share_url_type: x.share_url_type || '',
            }
          }) || [],
      }

      formikRef.current?.setFieldValue('shareSetting', shareSetting, false)
      formikRef.current?.setFieldValue('model', model, false)
      formikRef.current?.setFieldValue('carouselIndex', 0, false)
      formikRef.current?.setFieldValue(
        'schedulePreset',
        model.scheduled_for ? 'custom' : 'now',
        false,
      )
    }
  }, [
    share,
    socialAccounts.defaultFacebookPageId,
    socialAccounts.defaultTwitterAccountId,
    socialAccounts.defaultInstagramPageId,
    shareSetting,
  ])

  function handleClose() {
    closeShareDialog()

    formikRef.current?.setErrors({})
    formikRef.current?.setTouched({})
  }

  async function handleDelete() {
    if (
      editShareId &&
      (await confirm({ content: I18n.t('shareDialog.confirm.deleteShare') }))
    ) {
      publish('shareDialogDeleted', {
        id: editShareId,
        channel: share?.channel,
      })
      deleteShare({
        variables: {
          id: editShareId,
          shareType: `${shareSetting?.share_type}_shares`,
        },
      })
        .then(() => {
          enqueueSnackbar(
            I18n.t('shareDialog.notifications.deleteShareSuccess'),
            {
              variant: 'success',
            },
          )
          invalidateApolloQuery(['getShares', 'getCalendarItems'])
          handleClose()
        })
        .catch((error) => {
          console.error(error)
          enqueueSnackbar(
            I18n.t('shareDialog.notifications.deleteShareError'),
            {
              variant: 'error',
            },
          )
        })
    }
  }

  const canEdit = share?.status === 'scheduled'

  return (
    <Formik<ShareSettingDialogFormValues>
      onSubmit={async (values, formikActions) => {
        if (!shareSetting || !share || !editShareId) {
          return
        }
        formikActions.setSubmitting(true)

        const payload = buildShareDialogPayload({
          values,
          shareSetting,
          share: {
            ...share,
            id: editShareId,
          },
        })

        const eventName = `update_${editShareType}_share`
        const commonEventData = {
          scheduled: values.model.scheduled_for,
          success: false,
          share_id: editShareId,
          share_setting_id: shareSetting?.id,
          brand_id: shareSetting?.campaign?.brand?.id,
          retailer_id: retailer?.id,
          campaign_id: shareSetting?.campaign?.id,
          channel: editShareType,
          url: window.location.href,
        }

        try {
          const result = await updateShare({
            variables: payload,
          })
          if (result.data?.updateShare) {
            commonEventData.success = true
            publish('shareDialogUpdated', {
              id: editShareId,
              channel: share?.channel,
            })
            enqueueSnackbar(I18n.t('shareDialog.notifications.updateSuccess'), {
              variant: 'success',
            })
            invalidateApolloQuery(['getShare', 'getShares', 'getCalendarItems'])
          }
          handleClose()
        } catch (e) {
          console.error(e)
          enqueueSnackbar(I18n.t('shareDialog.notifications.updateError'), {
            variant: 'error',
          })
        }
        analytics.track({
          event: 'Action',
          type: eventName,
          data: commonEventData,
        })
        formikActions.setSubmitting(false)
      }}
      initialValues={{
        model: {
          share_url_type: '',
          allow_retailer_url_override: false,
          message: '',
          link: '',
          facebookPageId: '-1',
          twitterAccountId: '-1',
          instagramPageId: '-1',
          child_attachments: [],
          scheduled_for: '',
          subject: '',
          selected_imports: [],
        },
        shareSetting: undefined,
        schedulePreset: '',
        carouselIndex: 0,
      }}
    >
      {(formikProps) => {
        formikRef.current = formikProps
        const changedSchedule =
          formikProps.values.model.scheduled_for !== share?.scheduled_for
        return (
          <MobileFullScreenDialog
            open={!!editShareId || !!editShareId}
            onClose={() => {
              handleClose()
            }}
            fullWidth
            maxWidth="md"
          >
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} order={{ xs: 2, sm: 1 }}>
                  {share && (
                    <SocialPreview
                      share={{
                        ...share,

                        subject: formikProps.values.model.subject,
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6} order={{ xs: 1, sm: 2 }}>
                  {canEdit && (
                    <>
                      <Typography variant="h6">
                        {I18n.t('shareDialog.schedule.status', {
                          dateTime: formatDate(share?.scheduled_for),
                        })}
                      </Typography>
                      <ShareSettingForm
                        scheduleLabelOverride={
                          changedSchedule
                            ? I18n.t('shareDialog.schedule.altLabel')
                            : undefined
                        }
                        loading={loading || socialAccounts.loading}
                      />
                    </>
                  )}

                  {!canEdit && share && (
                    <ShareStats
                      share={share}
                      retailerAccountSlug={retailerAccountSlug || ''}
                    />
                  )}
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  handleClose()
                }}
              >
                {canEdit ? I18n.t('shared.cancel') : I18n.t('shared.close')}
              </Button>

              {canEdit && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    handleDelete()
                  }}
                >
                  {I18n.t('shared.delete')}
                </Button>
              )}

              {canEdit && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={formikProps.isSubmitting || !formikProps.isValid}
                  onClick={() => {
                    formikProps.submitForm()
                  }}
                >
                  {changedSchedule
                    ? I18n.t('shareDialog.submit.reschedule')
                    : I18n.t('shareDialog.submit.update')}
                </Button>
              )}
            </DialogActions>
          </MobileFullScreenDialog>
        )
      }}
    </Formik>
  )
}

export default ShareDialog
